import { useEffect, useState } from 'react';
import { Button, IconButton, Select, TextField } from '@dugzi/ui';
import { Plus, X } from 'lucide-react';
import { api } from '../lib/api';
import type { AddressParts, Labelled } from '../lib/types';

/** One country's address: its fields line by line (A street, Z postcode, C town, S region) and their names there. */
interface Country { code: string; name: string; lines: string[]; postcode: string; region: string | null }
interface Formats { home: string; countries: Country[] }

let formats: Promise<Formats> | null = null;
const loadFormats = () => (formats ??= api<Formats>('/api/address-formats').catch((e) => { formats = null; throw e; }));

const KEY = { A: 'street', Z: 'postcode', C: 'city', S: 'region' } as const;
type Field = keyof typeof KEY;

/** Addresses with their fields in each country's own order (Contacts PRD "Now": postcode before the town in the
 *  Netherlands and Germany, after it in the UK). Contacts writes the address line from these when it saves. */
export function AddressFields({ items, onChange }: { items: Labelled[]; onChange(v: Labelled[]): void }) {
  const [f, setF] = useState<Formats | null>(null);
  useEffect(() => { let live = true; loadFormats().then((x) => { if (live) setF(x); }).catch(() => {}); return () => { live = false; }; }, []);
  const home = f?.home || 'NL';
  const blank = (): Labelled => ({ value: '', label: null, parts: { country: home } });
  const rows = items.length ? items : [blank()];
  // A card saved before addresses had parts keeps its line as the street until someone edits it.
  const partsOf = (it: Labelled): AddressParts => it.parts ?? { street: it.value, country: home };
  const put = (i: number, it: Labelled, parts: AddressParts) => {
    // A one-line address saved before addresses had parts stays as it is until someone really changes a field (a
    // picker reporting its starting value is not a change).
    if (!it.parts) {
      const was = partsOf(it);
      const keys: (keyof AddressParts)[] = ['street', 'postcode', 'city', 'region', 'country'];
      if (keys.every((k) => (was[k] ?? '') === (parts[k] ?? ''))) return;
    }
    const value = [parts.street, parts.postcode, parts.city, parts.region].map((x) => (x ?? '').trim()).filter(Boolean).join(', ');
    onChange(rows.map((x, j) => (j === i ? { ...it, value, parts } : x)));
  };
  return (
    <fieldset className="multi addresses">
      <legend>Address</legend>
      {rows.map((it, i) => {
        const parts = partsOf(it);
        const c = f?.countries.find((x) => x.code === (parts.country || home));
        const name = (k: Field) => (k === 'A' ? 'Street and number' : k === 'Z' ? c?.postcode ?? 'Postcode' : k === 'C' ? 'Town or city' : c?.region ?? 'Region');
        const lines = c?.lines ?? ['A', 'Z C'];
        return (
          <div key={i} className="address" data-testid={`address-${i}`}>
            <div className="address__head">
              <Select label={i === 0 ? 'Country' : `Country ${i + 1}`} value={parts.country || home} onValueChange={(v) => put(i, it, { ...parts, country: String(v) })}
                options={(f?.countries ?? []).map((x) => ({ value: x.code, label: x.name }))} testId={`address-${i}-country`} />
              <TextField label="Kind" placeholder="home, work…" value={it.label ?? ''} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...it, label: e.target.value || null } : x)))} />
              {rows.length > 1 && <IconButton label={`Remove address ${i + 1}`} icon={<X aria-hidden size={16} />} onClick={() => onChange(rows.filter((_, j) => j !== i))} />}
            </div>
            {lines.map((line, n) => (
              <div key={n} className="address__line">
                {[...line].filter((ch): ch is Field => ch in KEY).map((k) => (
                  <TextField key={k} label={name(k)} value={parts[KEY[k]] ?? ''} autoComplete="off" data-testid={`address-${i}-${KEY[k]}`}
                    onChange={(e) => put(i, it, { ...parts, [KEY[k]]: e.target.value })} className={`address__${KEY[k]}`} />
                ))}
              </div>
            ))}
          </div>
        );
      })}
      <Button size="sm" variant="ghost" icon={<Plus aria-hidden />} onClick={() => onChange([...rows, blank()])}>Add address</Button>
    </fieldset>
  );
}
